<template>
  <div
    v-if="store.hasItems"
    ref="rootRef"
    class="onboarding-widget"
  >
    <button
      ref="buttonRef"
      type="button"
      class="onboarding-widget__button"
      :title="t('onboarding.buttonTooltip')"
      aria-haspopup="dialog"
      :aria-expanded="isOpen"
      @click="toggle"
    >
      <OnboardingChecklistIcon class="onboarding-widget__icon" />
      <span
        v-if="remainingCount > 0"
        class="onboarding-widget__badge"
      >
        {{ remainingCount }}
      </span>
    </button>

    <!-- На телефоне список открывается окном во весь экран и живёт в body, а не
         рядом с язычком (CAB-746). Две причины, обе жёсткие: у язычка есть
         transform, а он делает предка содержащим блоком для position: fixed
         внутри — «лист во всю ширину» разворачивался внутри 48px язычка узкой
         полосой у правого края; и z-index язычка заводит стекинг-контекст, из
         которого окно не может подняться над подсказками тура (10002–10020).
         Цель телепорта — body, она есть всегда: цель, появляющаяся по v-if,
         роняет Vue в moveTeleport при смене класса устройства (CAB-607). -->
    <Teleport
      to="body"
      :disabled="!isMobile"
    >
      <div
        v-if="isOpen"
        ref="panelRef"
        class="onboarding-widget__popover"
        :class="{ 'onboarding-widget__popover--modal': isMobile }"
        role="dialog"
        :aria-modal="isMobile ? 'true' : undefined"
        :aria-label="t('onboarding.title')"
      >
        <div class="onboarding-widget__header">
          <span class="onboarding-widget__title">{{ t("onboarding.title") }}</span>
          <button
            ref="closeRef"
            type="button"
            class="onboarding-widget__close"
            :title="t('onboarding.closeTooltip')"
            :aria-label="t('onboarding.closeTooltip')"
            @click="close"
          >
            <CloseIcon />
          </button>
        </div>

        <div class="onboarding-widget__progress-text">
          {{ t("onboarding.progressOf", { current: store.completedCount, total: store.totalCount }) }}
        </div>
        <div class="onboarding-widget__progress-bar">
          <div
            class="onboarding-widget__progress-fill"
            :style="{ width: progressPercent + '%' }"
          />
        </div>

        <ul class="onboarding-widget__list">
          <li
            v-for="item in store.items"
            :key="item.id"
            class="onboarding-widget__item"
            :class="{
              'onboarding-widget__item--completed': item.status === 'completed',
              'onboarding-widget__item--highlighted': item.id === highlightedItemId,
            }"
          >
            <span class="onboarding-widget__item-marker">
              <CheckmarkIcon v-if="item.status === 'completed'" />
            </span>
            <span class="onboarding-widget__item-name">
              {{ item.name }}
              <span
                v-if="item.status !== 'completed' && item.targetValue > 1"
                class="onboarding-widget__item-progress"
              >
                {{ item.counterValue }}/{{ item.targetValue }}
              </span>
            </span>
            <span class="onboarding-widget__item-reward">
              {{ t("onboarding.rewardAmount", { amount: item.rewardAmount }) }}
            </span>
          </li>
        </ul>

        <!-- ТЕСТ ОНБОРДИНГ §12 -->
        <p class="onboarding-widget__expiry-note">{{ t("onboarding.expiryNote") }}</p>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, nextTick, watch, onMounted, onBeforeUnmount } from "vue";
import { useI18n } from "vue-i18n";
import { useOnboardingStore } from "../store/onboarding";
import { useGuidanceStore } from "../store/guidance";
import { useBreakpoints } from "../common/composables/useBreakpoints";
import OnboardingChecklistIcon from "../assets/icons/OnboardingChecklistIcon.vue";
import CheckmarkIcon from "../assets/icons/CheckmarkIcon.vue";
import CloseIcon from "../assets/icons/CloseIcon.vue";

const { t } = useI18n();
const store = useOnboardingStore();
const guidanceStore = useGuidanceStore();
const { isMobile } = useBreakpoints();

const rootRef = ref<HTMLElement | null>(null);
const panelRef = ref<HTMLElement | null>(null);
const buttonRef = ref<HTMLElement | null>(null);
const closeRef = ref<HTMLElement | null>(null);
const isOpen = ref(false);
const highlightedItemId = ref<string | null>(null);

// Панель квестов разворачивается сама один раз, как только начинается
// онбординг (критичная правка) — дальше клиент управляет ей сам, toggle/close
// её больше не трогают. activeKind резолвится асинхронно (после
// clientContextStore.load() в MainLayout), поэтому не статичный initial value,
// а watch с { immediate: true }.
let hasAutoOpened = false;
watch(
  () => guidanceStore.activeKind === "onboarding" && guidanceStore.isActive,
  (isOnboardingActive) => {
    // На телефоне список раскрывается окном во весь экран — автораскрытие
    // накрыло бы первый же шаг онбординга целиком: язычок со счётчиком виден,
    // откроет сам, когда захочет.
    if (isOnboardingActive && !hasAutoOpened && !isMobile.value) {
      hasAutoOpened = true;
      isOpen.value = true;
    }
  },
  { immediate: true },
);

// Клик по наградному тосту (useOnboardingRewardWatcher → notifyReward →
// store.requestOpen): открыть панель и подсветить выполненный квест, даже
// если панель уже открыта или клиент уже сворачивал её раньше сам.
watch(
  () => store.openRequestToken,
  () => {
    isOpen.value = true;
    highlightedItemId.value = store.highlightedQuestId;
  },
);

// Подсветка живёт только пока панель открыта: снимаем её на любом пути
// закрытия (крестик, toggle, клик мимо), иначе следующее открытие вручную
// покажет устаревшую подсветку.
watch(isOpen, (open) => {
  if (!open) {
    highlightedItemId.value = null;
  }
});

// Окно во весь экран забирает фокус на себя и отдаёт его язычку при закрытии:
// без этого фокус остаётся на странице под окном, и следующий Tab уводит
// человека в невидимый контент.
watch(isOpen, async (open) => {
  if (!isMobile.value) return;
  await nextTick();
  (open ? closeRef.value : buttonRef.value)?.focus();
});

const remainingCount = computed(() => store.totalCount - store.completedCount);
const progressPercent = computed(() =>
  store.totalCount === 0 ? 0 : Math.round((store.completedCount / store.totalCount) * 100),
);

function toggle(): void {
  isOpen.value = !isOpen.value;
  if (isOpen.value) {
    store.load(true);
  }
}

function close(): void {
  isOpen.value = false;
}

// Панель на телефоне вынесена телепортом из корня виджета, поэтому «мимо» —
// это мимо обоих узлов: и язычка, и самой панели. По одному rootRef любой клик
// внутри открытого окна закрывал бы его.
function isInsideWidget(target: Node | null): boolean {
  if (!target) return false;
  return Boolean(rootRef.value?.contains(target) || panelRef.value?.contains(target));
}

function handleClickOutside(event: MouseEvent): void {
  if (isOpen.value && !isInsideWidget(event.target as Node | null)) {
    isOpen.value = false;
  }
}

function handleKeydown(event: KeyboardEvent): void {
  if (isOpen.value && event.key === "Escape") {
    isOpen.value = false;
  }
}

onMounted(() => {
  document.addEventListener("click", handleClickOutside);
  document.addEventListener("keydown", handleKeydown);
});

onBeforeUnmount(() => {
  document.removeEventListener("click", handleClickOutside);
  document.removeEventListener("keydown", handleKeydown);
});
</script>
